// ═══ 업무별 기록 — 업체 고르기 팝업 (직원 포함) ═══
// 서버 계약 (routes/workRecords.js, DESIGN §2·§7.5):
//   GET  /work-records/units?q=  → {success, units:[{unitId, name, accountId, accountName, accountKind}]}  (잔액 없음)
//   POST /work-records/units {name} → {success, unit:{unitId, name, accountId}}
//        409 {code:'NAME_EXISTS', unitId?, unitName?} = 같은 이름 업체가 이미 있다.
//        unitId·unitName 은 연결 가능한 기존 업체가 있을 때만 온다 → 그때만 [이 업체로 …], 아니면 검색 안내.
//        409 {code:'NAME_EXISTS', broker:true, accountName} = 그룹(대행사) 계정과 이름이 같다 → 그룹 소속 업체 검색
//        또는 구분되는 이름으로 만들기 (이름 칸은 계속 편집 가능).
// onPick 은 unitId > 0 일 때만 부른다 (0 = 연결 해제라 이 팝업의 결과가 될 수 없다).
// 직원도 쓰는 화면이라 기존 거래처·청구 계정 API(직원 403)는 부르지 않고 /work-records/units 만 쓴다.
// 검색 = 250ms debounce · Enter 는 keyup (한글 IME 조합 확정 엔터 — ClientScopeCombo 관례).
// 흐름: 결과 행을 눌러 고른 뒤 [confirmLabel] → onPick(unit). 새로 만든 업체는 만들자마자 onPick.
//
// props: title · hint · confirmLabel('연결') · initialName(새 업체 이름 기본값) · busy · onPick(unit) · onClose · showToast
import React, { useState, useEffect } from 'react';
import { API, authFetch } from '../utils/api';
import { useIsMobile } from '../utils/useMobile';
import { is } from './SettlementItemDrawer';

var overlay = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: 16 };

export default function RecordsUnitPicker(props) {
  var isMobile = useIsMobile();
  var showToast = props.showToast || function () {};
  var onClose = props.onClose || function () {};
  var confirmLabel = props.confirmLabel || '연결';
  var [q, setQ] = useState('');
  var [units, setUnits] = useState([]);
  var [loading, setLoading] = useState(true);
  var [picked, setPicked] = useState(null);
  var [newName, setNewName] = useState(null); // null = 만들기 칸 닫힘
  var [exists, setExists] = useState(null);   // {unitId, name} — 409 NAME_EXISTS
  var [creating, setCreating] = useState(false);
  var busy = !!props.busy || creating;
  var pick = function (u) { if (u && (parseInt(u.unitId, 10) || 0) > 0) props.onPick(u); };

  useEffect(function () {
    var alive = true;
    var t = setTimeout(function () {
      setLoading(true);
      authFetch(API + '/work-records/units?q=' + encodeURIComponent(q.trim()))
        .then(function (r) { return r.json(); })
        .then(function (d) {
          if (!alive) return;
          setUnits(d && d.success && Array.isArray(d.units) ? d.units : []);
          setLoading(false);
        })
        .catch(function () { if (!alive) return; setUnits([]); setLoading(false); });
    }, 250);
    return function () { alive = false; clearTimeout(t); };
  }, [q]);

  var create = function () {
    var name = String(newName || '').trim();
    if (!name || busy) return;
    setCreating(true); setExists(null);
    authFetch(API + '/work-records/units', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name: name }) })
      .then(function (r) { return r.json().catch(function () { return {}; }).then(function (d) { return { status: r.status, data: d || {} }; }); })
      .then(function (res) {
        setCreating(false);
        var d = res.data;
        if (res.status === 409 && d.code === 'NAME_EXISTS') {
          var uid = parseInt(d.unitId, 10) || 0;
          setExists({ unitId: uid, name: uid > 0 ? (d.unitName || name) : name, broker: uid <= 0 && !!d.broker, accountName: d.accountName || name });
          if (uid <= 0) setQ(name);   // 연결할 업체를 검색 결과에서 고르게
          return;
        }
        if (!d.success || !d.unit || !((parseInt(d.unit.unitId, 10) || 0) > 0)) { showToast(d.error || '업체를 만들지 못했습니다', 'err'); return; }
        showToast('업체를 만들었습니다');
        pick(d.unit);
      })
      .catch(function () { setCreating(false); showToast('서버 오류', 'err'); });
  };

  var rowSt = function (on) {
    return { display: 'flex', alignItems: 'baseline', gap: 8, width: '100%', textAlign: 'left', padding: isMobile ? '11px 12px' : '9px 11px',
      border: 'none', borderBottom: '1px solid #f1f5f9', background: on ? '#eff6ff' : '#fff', cursor: 'pointer', fontFamily: 'inherit' };
  };

  return (<div style={overlay} onClick={function () { if (!busy) onClose(); }}>
    <div role="dialog" aria-label={props.title || '업체 선택'} onClick={function (e) { e.stopPropagation(); }}
      style={{ background: '#fff', borderRadius: 12, padding: isMobile ? 16 : 20, width: 460, maxWidth: '94vw', maxHeight: '90vh', overflowY: 'auto', fontFamily: 'Pretendard,sans-serif' }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
        <div style={{ flex: 1, fontSize: 15, fontWeight: 800, color: '#1B3A5C' }}>{props.title || '업체 선택'}</div>
        <button onClick={function () { if (!busy) onClose(); }} aria-label="닫기" style={{ border: 'none', background: 'none', fontSize: 18, color: '#94a3b8', cursor: 'pointer', minWidth: 32, minHeight: 32 }}>✕</button>
      </div>
      {!!props.hint && <p style={{ margin: '0 0 10px', fontSize: 12.5, color: '#64748b', lineHeight: 1.5 }}>{props.hint}</p>}

      <input value={q} placeholder="업체 검색" aria-label="업체 검색" style={is}
        onChange={function (e) { setQ(e.target.value); }}
        onKeyUp={function (e) {
          // keyup — 한글 IME 조합 확정 엔터도 잡는다 (keydown 은 isComposing 으로 소비됨)
          if (e.key === 'Enter' && units.length) setPicked(units[0]);
        }} />
      <div aria-label="업체 목록" style={{ marginTop: 8, maxHeight: 260, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 9 }}>
        {units.map(function (u) {
          var on = !!picked && picked.unitId === u.unitId;
          return (<button key={u.unitId} type="button" aria-label={'업체 ' + u.name} style={rowSt(on)} onClick={function () { setPicked(u); }}>
            <span style={{ fontSize: 13, fontWeight: on ? 800 : 600, color: on ? '#1d4ed8' : '#334155' }}>{u.name}</span>
            {!!u.accountName && u.accountName !== u.name && <span style={{ fontSize: 11.5, color: '#94a3b8' }}>· {u.accountName}</span>}
          </button>);
        })}
        {!units.length && <div style={{ padding: 16, textAlign: 'center', fontSize: 12, color: '#94a3b8' }}>{loading ? '검색 중…'
          : (!!exists && !(exists.unitId > 0) && !exists.broker) ? '검색 결과가 없습니다.' : '검색 결과가 없습니다. 아래에서 새 업체를 만들 수 있습니다.'}</div>}
      </div>

      {!!picked && <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, padding: '9px 11px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 9 }}>
        <span style={{ flex: 1, fontSize: 13, color: '#1d4ed8', fontWeight: 700 }}>선택: {picked.name}</span>
        <button className="btn btn-blue btn-sm" disabled={busy} onClick={function () { pick(picked); }}>{busy ? '처리 중…' : confirmLabel}</button>
      </div>}

      <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid #f1f5f9' }}>
        {newName === null
          ? <button className="btn btn-outline btn-sm" onClick={function () { setNewName(String(props.initialName || q || '').trim()); setExists(null); }}>＋ 새 업체 만들기</button>
          : <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>새 업체 이름</div>
            <div style={{ display: 'flex', gap: 6 }}>
              <input value={newName} aria-label="새 업체 이름" maxLength={100} style={Object.assign({}, is, { flex: 1 })}
                onChange={function (e) { setNewName(e.target.value); setExists(null); }} />
              <button className="btn btn-blue btn-sm" disabled={busy || !String(newName).trim()} onClick={create}>{creating ? '만드는 중…' : '만들고 ' + confirmLabel}</button>
            </div>
            {!!exists && <div data-testid="name-exists" style={{ marginTop: 8, padding: '9px 11px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 9, fontSize: 12.5, color: '#92400e' }}>
              {exists.unitId > 0
                ? <React.Fragment>
                  같은 이름의 업체 <b>{exists.name}</b> 이(가) 이미 있습니다. 그 업체로 {confirmLabel}할 수 있습니다.
                  <div style={{ marginTop: 6 }}>
                    <button className="btn btn-blue btn-sm" disabled={busy} onClick={function () { pick({ unitId: exists.unitId, name: exists.name }); }}>이 업체로 {confirmLabel}</button>
                  </div>
                </React.Fragment>
                : exists.broker
                  ? '\'' + exists.accountName + '\' 그룹과 이름이 같습니다. 검색에서 그룹 소속 업체를 고르거나, 이름을 구분해서 만들어 주세요.'
                  : '같은 이름의 업체가 이미 있습니다. 검색해서 선택해 주세요.'}
            </div>}
          </div>}
      </div>
    </div>
  </div>);
}
